import React, { Fragment, useEffect, useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

// Components

import AdditionalInformationSummary from './pageIncludes/summary/AdditionalInformationSummary';
import AttestationsSummary from './pageIncludes/summary/AttestationsSummary';
import ComparisonSwitcher from './pageIncludes/summary/ComparisonSwitcher';
import FamilyAndHouseholdSummary from './pageIncludes/summary/FamilyAndHouseholdSummary';
import HouseholdCommonSummary from './pageIncludes/summary/HouseholdCommonSummary';
import IncomeInformationSummary from './pageIncludes/summary/IncomeInformationSummary';
import PageHeader from '../headers/PageHeader';
import PrimaryContactPersonSummary from './pageIncludes/summary/PrimaryContactPersonSummary';
import SsapSummaryErrorBoundary from '@components/ssap/SsapSummaryErrorBoundary';

// ActionCreators

import {
    clearMethods,
    clearPreviousData,
    clearSsapSummary,
    fetchAllBloodRelationships,
    fetchEnrollments,
    fetchPreviousApplication,
    fetchTribes,
    sendSummaryToInbox,
    setMethods,
    showExtraFinalSummary,
    toggleUpdatedFieldsVisibility
} from '@actions/ssap/ssapActionCreators';

import {
    fetchCommunicationPreferences
} from '@actions/individualPortal/preferences/communicationPreferencesActionCreators';

// Selectors

import {
    selectIfExtraFinalSummaryShown,
    selectIfFinalReviewPageActive,
    selectIfOnlyUpdatedFieldsShown,
    selectSsapPreviousData,
    selectIfPreviousDataLoaded,
    selectIfSummaryCrashed,
    selectIfShouldFetchEnrollments,
    selectIfRenewalFlow,
    selectIfSsapOpenInReviewMode,
    useExchangeAndProgramNames,
    selectAddressVisibilityDueToGeneralDelivery,
    selectIfSogiFeatureEnabled,
    selectIfLanguagesLoaded
} from '@reducers/appReducer';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { SSAP_PDF_SUBMISSION_DATE_FORMAT_OPTIONS } from '@utils/dates/dateFormats.utils';

// Constants

import { configs } from '@constants/configs/configs';
import { flowTypes as FLOW_TYPES } from '@constants/ssap/flows/flowTypes';
import { PAGES } from '@constants/ssap/flows/pagesTypes';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - Summary page for 'Sign and Review' Section
 *
 */
const Page3_16 = props => {

    const {
        actions: {
            clearMethods,
            fetchAllBloodRelationships,
            fetchEnrollments,
            fetchPreviousApplication,
            fetchCommunicationPreferences,
            fetchTribes,
            sendSummaryToInbox,
            setMethods
        },
        data,
        data: {
            applicationGuid,
            applicationSignatureDate,
            applyingForFinancialAssistanceIndicator,
            coverageYear,
            householdCaseId
        },
        environment: {
            assets: {
                LOGO
            },
            exchangeName,
            isState,
            language,
            localDates: {
                serverDate
            }
        },
        flow: {
            flowType
        },
        formName,
        adjustedFlow,
        allStatuses,
        taxHousehold,
        householdMembers,
        messages,
        relations,
        relations: {
            loadedRelationsWholeList = []
        },
        stateCode,
        tribes
    } = props;

    // Props from PagesContainer
    const {
        alwaysDisplayMedicaidQuestions,
        inlineCurrentIncomeEnabled,
        medicaidTerminationFeatureEnabled
    } = props;

    const areOnlyUpdatedFieldsShown = useSelector(selectIfOnlyUpdatedFieldsShown);
    const isExtraSummaryPageShown = useSelector(selectIfExtraFinalSummaryShown);
    const isFinalReviewPageActive = useSelector(selectIfFinalReviewPageActive);
    const isPreviousSsapApplicationLoaded = useSelector(selectIfPreviousDataLoaded);
    const isSummaryCrashed = useSelector(selectIfSummaryCrashed);
    const previousData = useSelector(selectSsapPreviousData);
    const shouldFetchEnrollments = useSelector(selectIfShouldFetchEnrollments);
    const isRenewalFlow = useSelector(selectIfRenewalFlow);
    const isSsapInReviewMode = useSelector(selectIfSsapOpenInReviewMode);
    const exchangeAndProgramNames = useExchangeAndProgramNames();
    const addressVisibilityDueToGeneralDelivery = useSelector(selectAddressVisibilityDueToGeneralDelivery);
    const isSogiFeatureEnabled = useSelector(selectIfSogiFeatureEnabled);
    const areLanguagesLoaded = useSelector(selectIfLanguagesLoaded);

    const {
        applicantGuid: pcpApplicantGuid,
        name,
    } = householdMemberTools.getPrimaryContact(householdMembers);

    const {
        buttonContainer: ButtonContainer,
        isUnemploymentIncomeAttestationEnabled,
        itfFeatureEnabled,
        pageName
    } = props;

    const [ container, setContainer ] = useState(null);

    const [ print, togglePrinting ] = useState({
        action: null,
        isComparisonToggled: false,
        isPrinting: false,
        withDiffs: isExtraSummaryPageShown
    });

    const isExtraFinalSummaryPage  = pageName === PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE;
    const isFinancialFlow = flowType === FLOW_TYPES.FINANCIAL_FLOW;

    const intlApi = useIntl();
    const statesNotInAlignWithMP = [ 'GA', 'ID', 'PA', 'NM'];
    const caseId = statesNotInAlignWithMP.includes(stateCode) ? intlApi.formatMessage({ id: 'ssap.pdf.case_id' }) : intlApi.formatMessage({ id: 'common.case_id' });
    const appId = statesNotInAlignWithMP.includes(stateCode )? intlApi.formatMessage({ id: 'ssap.pdf.app_id' }) : intlApi.formatMessage({ id: 'common.app_id' });
    useEffect(() => {

        const signatureDayjs = applicationSignatureDate
            ? dayjs(applicationSignatureDate)
            : null;
        const pdfSubmissionDate = signatureDayjs?.isValid()
            ? intlApi.formatDate(
                signatureDayjs.toDate(),
                SSAP_PDF_SUBMISSION_DATE_FORMAT_OPTIONS,
            )
            : intlApi.formatMessage({ id: 'ssap.pdf.ssap_not_submitted_yet' });

        const PDF_HEADER_WITH_STATE_LOGO = (
            <div className='pdf-header'>
                <img
                    src={ LOGO }
                    alt={ exchangeName }
                />
                <p>{ intlApi.formatMessage({ id: 'ssap.pdf.extracted' }, { exchangeName }) }</p>
                <div>
                    <p className='float-left'>
                        {  caseId }: { householdCaseId }
                        <br/>
                        {  appId }: { applicationGuid }
                    </p>
                    <p className='float-right'>
                        {
                            intlApi.formatMessage(
                                { id: 'ssap.pdf.submission_date' },
                                { submissionDate: pdfSubmissionDate },
                            )

                        }
                    </p>
                    <div/>
                </div>
            </div>
        );

        const script = document.createElement('script');

        (() => {
            script.type = 'application/javascript';
            script.src = configs.endpoints.externalUrls.html2PdfScript;
            document.head.appendChild(script);
        })();

        const printConfig = {
            ['.buttons-group']: {
                display: 'none'
            },
            ['h3']: {
                color: '#333333',
            },
            ['.header-btn']: {
                visibility: 'hidden'
            },
            ['.gi-alert__print_hidden']: {
                display: 'none'
            },
            ['p']: {
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
            },
            ['li']: {
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
            }
        };

        const payload = document.createElement('div');

        Promise.resolve(
            (() => {
                const headerHtml = ReactDOMServer.renderToStaticMarkup(PDF_HEADER_WITH_STATE_LOGO);
                const headerWrapper = document.createElement('div');
                headerWrapper.innerHTML = headerHtml;
                payload.prepend(headerWrapper);
                return payload;
            })()
        )
            .then(() => {
                [
                    '.extract',
                    '.extract2',
                    '.extract3'
                ].forEach(contentChunk => {
                    (
                        el => el
                            ? payload.appendChild(el.cloneNode(true))
                            : null
                    )(document.querySelector(contentChunk));
                });

                Object
                    .keys(printConfig)
                    .map(domElement => [...payload.querySelectorAll(domElement)])
                    .reduce((commonList, el) => [ ...commonList, ...el ], [])
                    .forEach(selected => {

                        const tagSelector = selected.nodeName.toLowerCase();

                        const classSelector = (className => className ? `.${className}` : undefined)(selected.className.split(' ').find(cl => Object.keys(printConfig).some(sel => String(sel).slice(1) === cl)));

                        const idSelector = Object.keys(printConfig).find(sel => String(sel).slice(1) === selected.id);

                        const commonStyleObj = [
                            printConfig[tagSelector],
                            printConfig[classSelector],
                            printConfig[idSelector]
                        ].filter(sel => sel)
                            .reduce(( commonStyleObj, styleObj ) => ({ ...commonStyleObj, ...styleObj }), {});

                        if(commonStyleObj) {
                            Object.entries(commonStyleObj).forEach(([cssProp, cssValue]) => {
                                selected.style[cssProp] = cssValue;
                            });
                        }
                    });
            })
            .then(() => {
                setContainer(payload);
            });

        const removeScript = () => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };

        return () => {
            removeScript();
        };

    }, [
        tribes.length,
        messages[language],
        isPreviousSsapApplicationLoaded,
        areOnlyUpdatedFieldsShown,
        isExtraSummaryPageShown,
        areLanguagesLoaded,
        language,
        applicationSignatureDate,
    ]);

    useEffect(() => {
        fetchCommunicationPreferences(null, false, true);
        loadedRelationsWholeList.length === 0 && fetchAllBloodRelationships();
        tribes.length === 0 && fetchTribes();
        shouldFetchEnrollments && fetchEnrollments();
        !isPreviousSsapApplicationLoaded
            && isFinalReviewPageActive
            && isExtraSummaryPageShown
            && fetchPreviousApplication(pcpApplicantGuid);
    }, []);

    useEffect(() => {
        return () => {
            if(isFinalReviewPageActive) {
                props.actions.clearSsapSummary();
                props.actions.clearPreviousData();
            }
        };
    }, [ ]);


    /**
     * Function that generates PDF for printing or saving
     *
     * @param {string} action - type of action to do after PDF generation has been finished
     */
    const handleAction = action => {

        const opt = {
            margin:       [.4, .5, .3, .5],
            filename:     `${normalizeNameOfPerson(name)} - Summary.pdf`,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2 },
            jsPDF:        { unit: 'in', format: 'a4' },
            pagebreak:    {
                avoid: ['span', 'p', 'li','.grid-row', '.grid-col-12', '.summary__label', '.text-break']
            }
        };

        switch(action) {
            case 'download':

                window.html2pdf().set(opt).from(container).output().save();

                isState.ID && sendSummaryToInbox();

                break;
            case 'print':

                window.html2pdf().set(opt).from(container).output('bloburl').then(res => {
                    window.open(res, '_blank').print();
                });
                break;
        }
    };

    const handleSummaryOutput = (...args) => {
        const [ action ] = args;

        if(print.withDiffs) {
            props.actions.toggleUpdatedFieldsVisibility(false);
            props.actions.showExtraFinalSummary(false);
            setTimeout(() => {
                togglePrinting(print => ({
                    ...print,
                    action,
                    isComparisonToggled: areOnlyUpdatedFieldsShown,
                    isPrinting: true
                }));
            });
        } else {
            togglePrinting(print => ({
                ...print,
                action,
                isComparisonToggled: areOnlyUpdatedFieldsShown,
                isPrinting: true
            }));
        }
    };

    useEffect(() => {
        if(print.isPrinting && isExtraSummaryPageShown === false) {
            handleAction(print.action);

            togglePrinting(print => ({
                ...print,
                action: null,
                isComparisonToggled: false,
                isPrinting: false
            }));

            if(print.isComparisonToggled) {
                props.actions.toggleUpdatedFieldsVisibility(true);
            }

            if(print.withDiffs) {
                props.actions.showExtraFinalSummary(true);
            }
        }
    }, [ print.isPrinting, isExtraSummaryPageShown ]);

    useEffect(
        () => {
            setMethods(
                'Page3_16',
                {
                    handleSummaryOutput
                }
            );

            return () => {
                clearMethods('Page3_16');
            };
        },
        [
            loadedRelationsWholeList,
            container,
            areOnlyUpdatedFieldsShown,
            isExtraSummaryPageShown
        ]
    );

    const indexMapper = useMemo(
        () => {
            let mapping = {
                currentValues: {},
                previousValues: {}
            };

            return data.taxHousehold.householdMember.reduce(
                (mapping, hhm, idx) => {
                    const { applicantGuid } = hhm;
                    const matchedIndex = previousData.taxHousehold.householdMember.findIndex(hhmFromPreviousData => {
                        const { applicantGuid: hhmFromPreviousDataApplicantGuid } = hhmFromPreviousData;
                        return hhmFromPreviousDataApplicantGuid === applicantGuid;
                    });

                    const currentIndexAsString = String(idx);
                    const previousIndexAsString = String (~matchedIndex ? matchedIndex : -1);

                    return {
                        ...mapping,
                        currentValues: {
                            ...mapping.currentValues,
                            [ currentIndexAsString ]: currentIndexAsString
                        },
                        previousValues: {
                            ...mapping.previousValues,
                            [ currentIndexAsString ]: previousIndexAsString
                        }
                    };
                },
                mapping
            );
        },
        [ data, previousData ]
    );

    return (
        <SsapSummaryErrorBoundary>
            <div
                className = 'fade-in'
                id = 'rsReview'
            >
                <div className = 'subsection' >
                    <ComparisonSwitcher
                        { ...{
                            actions: {
                                toggleUpdatedFieldsVisibility: props.actions.toggleUpdatedFieldsVisibility
                            },
                            areOnlyUpdatedFieldsShown,
                            formName,
                            isExtraFinalSummaryPage,
                            isExtraSummaryPageShown,
                            isSummaryCrashed
                        } }
                    />
                    <div className = 'extract' >
                        <div className = 'final-review-heading' >
                            <PageHeader
                                text = 'ssap.subheader.household_and_demo_information'
                                headerClassName = 'gi-heading-alt summary__header--no-border'
                                { ...(
                                    !isExtraFinalSummaryPage
                                        ? {
                                            additionalElement: (
                                                <Fragment>
                                                    <ButtonContainer
                                                        args = { [ 'download' ] }
                                                        onClick = { handleSummaryOutput }
                                                        type = 'downloadButton'
                                                    />
                                                    <ButtonContainer
                                                        args = { [ 'print' ] }
                                                        onClick = { handleSummaryOutput }
                                                        type = 'printButton'
                                                    />
                                                </Fragment>
                                            ),
                                            additionalElementClassName: 'header-btn'
                                        }
                                        : {
                                            additionalElement: (
                                                <ButtonContainer
                                                    args = { [ undefined, PAGES.PRIMARY_CONTACT_INFORMATION_PAGE ] }
                                                    type = 'editButton_secondary'
                                                />
                                            ),
                                            additionalElementClassName: 'header-btn'
                                        }
                                ) }
                            />
                        </div>
                        <div className = 'final-review-body break_after' >
                            <PrimaryContactPersonSummary
                                { ...{
                                    indexMapper
                                } }
                            />
                        </div>

                        <div className = 'final-review-body break_after' >
                            <HouseholdCommonSummary
                                { ...{
                                    exchangeName,
                                    indexMapper
                                } }
                            />
                        </div>

                        <FamilyAndHouseholdSummary
                            { ...{
                                addressVisibilityDueToGeneralDelivery,
                                applyingForFinancialAssistanceIndicator,
                                buttonContainer: ButtonContainer,
                                allStatuses,
                                exchangeAndProgramNames,
                                indexMapper,
                                isSogiFeatureEnabled,
                                isState,
                                medicaidTerminationFeatureEnabled,
                                relations,
                                taxHousehold,
                                householdMembers,
                                flowType,
                                serverDate,
                                stateCode,
                                tribes
                            } }
                        />
                    </div>

                    {
                        isFinancialFlow
                            ? (
                                <div className = 'extract2 break_before' >
                                    <div className = 'final-review-body' >
                                        <IncomeInformationSummary
                                            { ...{
                                                buttonContainer: ButtonContainer,
                                                coverageYear,
                                                householdMembers,
                                                isState,
                                                isUnemploymentIncomeAttestationEnabled,
                                                indexMapper,
                                                itfFeatureEnabled,
                                                pageName,
                                                previousData,
                                                taxHousehold
                                            } }
                                        />
                                    </div>
                                    <div className = 'final-review-body' >
                                        <AdditionalInformationSummary
                                            { ...{
                                                adjustedFlow,
                                                allStatuses,
                                                alwaysDisplayMedicaidQuestions,
                                                buttonContainer: ButtonContainer,
                                                householdMembers,
                                                indexMapper,
                                                inlineCurrentIncomeEnabled,
                                                isRenewalFlow,
                                                isState,
                                                stateCode,
                                                itfFeatureEnabled,
                                                previousData,
                                                serverDate,
                                                taxHousehold
                                            } }
                                        />
                                    </div>
                                </div>
                            )
                            : null
                    }

                    {
                        isSsapInReviewMode
                            ? (
                                <div className = 'extract3' >
                                    <AttestationsSummary
                                        data = { data }
                                        exchangeName = { exchangeName }
                                        flowType = { flowType }
                                        isState = { isState }
                                        exchangeAndProgramNames={ exchangeAndProgramNames }
                                    />
                                </div>
                            )
                            : null
                    }
                </div>
            </div>
        </SsapSummaryErrorBoundary>
    );
};

const mapStateToProps = state => {

    const sortedHouseholds = state
        .ssap
        .data
        .taxHousehold
        .householdMember.sort((a, b) => a.personId - b.personId);

    return {
        messages: state.common.messages,

        adjustedFlow: state.ssap.adjustedFlow,
        data: state.ssap.data,
        environment: state.common.environment,
        flow: state.ssap.flow,
        householdMembers: sortedHouseholds,
        relations: state.ssap.relations,

        stateCode: state
            .common
            .environment
            .stateCode,

        taxHousehold: state
            .ssap
            .data
            .taxHousehold,

        tribes: state.ssap.tribes
    };
};

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        clearMethods,
        clearPreviousData,
        clearSsapSummary,
        fetchAllBloodRelationships,
        fetchEnrollments,
        fetchPreviousApplication,
        fetchCommunicationPreferences,
        fetchTribes,
        sendSummaryToInbox,
        setMethods,
        showExtraFinalSummary,
        toggleUpdatedFieldsVisibility
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page3_16);
